import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Clock, Navigation, Flag } from 'lucide-react';
import GuestBookingForm from '../components/GuestBookingForm';
import { scrollToTop } from '../utils/scrollUtils';

const BookingPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Book a Ride - AbleGo';
    scrollToTop('instant');
  }, []);

  return (
    <>
      <Helmet>
        <title>Book a Ride - AbleGo</title>
        <meta
          name="description"
          content="Book accessible transport. Enter your trip, choose a time, confirm — we’ll reply by email."
        />
      </Helmet>

      <div className="pt-20">
        <section className="py-10 md:py-14 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-dark-900 dark:to-dark-800 booking-form">
          <div className="container mx-auto px-4 md:px-6 max-w-6xl">
            <div className="text-center mb-8 md:mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-50 mb-2">
                Book a ride
              </h1>
              <div
                className="mx-auto max-w-2xl rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-left dark:border-amber-800 dark:bg-amber-950/40"
                role="status"
              >
                <p className="flex items-start gap-3 text-sm md:text-base text-amber-950 dark:text-amber-100">
                  <Flag
                    className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                    aria-hidden
                  />
                  <span>
                    <span className="font-semibold">Please note:</span> We currently have limited
                    vehicle availability and can only guarantee bookings within Middlesbrough and
                    Teesside.
                  </span>
                </p>
              </div>
            </div>
            <GuestBookingForm />
          </div>
        </section>

        <section className="py-10 bg-white dark:bg-dark-800 border-y border-gray-100 dark:border-dark-700">
          <div className="container mx-auto px-4 md:px-6 max-w-3xl">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-center mb-6">
              Three steps
            </h2>
            <ol className="grid md:grid-cols-3 gap-6 text-center text-sm text-gray-700 dark:text-gray-300">
              <li className="flex flex-col items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600">
                  <MapPin className="w-5 h-5" />
                </span>
                <span className="font-medium text-gray-900 dark:text-gray-100">Places & time</span>
                <span className="text-gray-500 dark:text-gray-400">Where and when we pick you up</span>
              </li>
              <li className="flex flex-col items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-600">
                  <Clock className="w-5 h-5" />
                </span>
                <span className="font-medium text-gray-900 dark:text-gray-100">Extras</span>
                <span className="text-gray-500 dark:text-gray-400">Support workers & ride options if needed</span>
              </li>
              <li className="flex flex-col items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40 text-green-600">
                  <Navigation className="w-5 h-5" />
                </span>
                <span className="font-medium text-gray-900 dark:text-gray-100">Pay</span>
                <span className="text-gray-500 dark:text-gray-400">Card now or cash / transfer on the day</span>
              </li>
            </ol>
          </div>
        </section>

        <section className="py-10 bg-blue-600 text-white">
          <div className="container mx-auto px-4 text-center max-w-xl">
            <p className="font-semibold mb-2">Need help with this booking?</p>
            <p className="text-blue-100 text-sm mb-5">Call or email and we’ll assist.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:01642089958"
                className="px-5 py-2.5 bg-white text-blue-600 rounded-lg font-medium text-sm hover:bg-blue-50"
              >
                01642 089 958
              </a>
              <a
                href="mailto:hello@ablego.co.uk"
                className="px-5 py-2.5 border border-white/80 text-white rounded-lg font-medium text-sm hover:bg-white/10"
              >
                hello@ablego.co.uk
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BookingPage;
